import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  Lock,
  ArrowRight,
  Eye,
  EyeOff,
  CheckCircle2,
  ShieldAlert,
  AlertTriangle,
} from 'lucide-react';
import { theme } from '../styles/theme';
import LogoComponent from '../components/Logo';
import { AuthAPI } from '../services/api';

const Container = styled.div`
  min-height: 100vh;
  background: ${theme.colors.backgroundSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  width: 100%;
  max-width: 480px;
`;

const LogoContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: ${theme.spacing[6]};
  width: 135px;
  height: auto;
  margin-left: auto;
  margin-right: auto;
`;

const TitleBlock = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing[6]};
`;

const Title = styled.h1`
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]} 0;
  font-family: ${theme.typography.fontFamily.display};
  letter-spacing: -0.02em;
`;

const Subtitle = styled.p`
  color: ${theme.colors.textMuted};
  margin: 0;
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.5;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[5]};
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  text-align: left;
`;

const Label = styled.label`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textPrimary};
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const InputIcon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.textMuted};
  z-index: 1;
  display: flex;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[3]} ${theme.spacing[10]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  background: ${theme.colors.surface};
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100] || 'rgba(0,0,82,0.12)'};
  }
  &::placeholder { color: ${theme.colors.textMuted}; }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing[3]};
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  padding: 0;

  &:hover { color: ${theme.colors.textPrimary}; }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) { background: ${theme.colors.primary[600]}; }
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;

const ErrorBanner = styled.div`
  background: ${theme.colors.error?.[100] || '#fee'};
  color: ${theme.colors.error?.[700] || '#c00'};
  border: 1px solid ${theme.colors.error?.[100] || '#fbb'};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  gap: ${theme.spacing[3]};
  align-items: flex-start;
  line-height: 1.5;
  margin-top: ${theme.spacing[4]};

  svg { flex-shrink: 0; margin-top: 2px; }
`;

const FooterRow = styled.p`
  margin: ${theme.spacing[6]} 0 0 0;
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};

  a {
    color: ${theme.colors.primary[500]};
    text-decoration: none;
    font-weight: ${theme.typography.fontWeight.medium};
    &:hover { text-decoration: underline; }
  }
`;

const SuccessIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.success?.[100] || '#dcfce7'};
  color: ${theme.colors.success?.[700] || '#15803d'};
  margin-bottom: ${theme.spacing[4]};
`;

const InvalidIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.warning?.[100] || '#fef3c7'};
  color: ${theme.colors.warning?.[700] || '#a16207'};
  margin-bottom: ${theme.spacing[4]};
`;

const Message = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.6;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const PrimaryLinkButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: white;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  text-decoration: none;
  transition: all ${theme.transitions.base};

  &:hover { background: ${theme.colors.primary[600]}; }
`;

type State = 'verifying' | 'invalid' | 'ready' | 'submitting' | 'done';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [state, setState] = useState<State>('verifying');
  const [tokenEmail, setTokenEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // On mount, ask the backend whether the token is still valid before
  // showing the form. Saves the user from typing a new password into a
  // dead link and only finding out on submit.
  useEffect(() => {
    if (!token) {
      setState('invalid');
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await AuthAPI.verifyResetToken(token);
      if (cancelled) return;

      if (result.success && result.data?.valid) {
        setTokenEmail(result.data.email || null);
        setState('ready');
      } else {
        setState('invalid');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setState('submitting');
    const result = await AuthAPI.resetPassword(token, password);

    if (result.success) {
      setState('done');
      // Auto-redirect to login after a short pause so they read the message.
      setTimeout(() => navigate('/login'), 4000);
      return;
    }

    setState('ready');
    setError(result.error || 'Could not reset password. The link may have expired.');
  };

  if (state === 'verifying') {
    return (
      <Container>
        <Card style={{ textAlign: 'center' }}>
          <LogoContainer><LogoComponent size={50} /></LogoContainer>
          <Title>Checking your link...</Title>
          <Subtitle>One moment.</Subtitle>
        </Card>
      </Container>
    );
  }

  if (state === 'invalid') {
    return (
      <Container>
        <Card style={{ textAlign: 'center' }}>
          <LogoContainer><LogoComponent size={50} /></LogoContainer>
          <InvalidIcon><AlertTriangle size={28} /></InvalidIcon>
          <Title>This link won't work.</Title>
          <Message>
            It's either expired (links last 30 minutes) or has already been
            used. Request a new one and try again.
          </Message>
          <PrimaryLinkButton to="/forgot-password">
            Request a new link <ArrowRight size={16} />
          </PrimaryLinkButton>
          <FooterRow>
            <Link to="/login">&larr; Back to sign in</Link>
          </FooterRow>
        </Card>
      </Container>
    );
  }

  if (state === 'done') {
    return (
      <Container>
        <Card style={{ textAlign: 'center' }}>
          <LogoContainer><LogoComponent size={50} /></LogoContainer>
          <SuccessIcon><CheckCircle2 size={28} /></SuccessIcon>
          <Title>Password updated.</Title>
          <Message>
            You can sign in now with your new password. Redirecting you to
            the sign-in page...
          </Message>
          <PrimaryLinkButton to="/login">
            Sign in <ArrowRight size={16} />
          </PrimaryLinkButton>
        </Card>
      </Container>
    );
  }

  // state === 'ready' or 'submitting'
  const submitting = state === 'submitting';

  return (
    <Container>
      <Card>
        <LogoContainer><LogoComponent size={50} /></LogoContainer>
        <TitleBlock>
          <Title>Choose a new password</Title>
          <Subtitle>
            {tokenEmail
              ? <>For <strong>{tokenEmail}</strong>. Eight characters minimum.</>
              : 'Eight characters minimum.'}
          </Subtitle>
        </TitleBlock>

        <Form onSubmit={handleSubmit}>
          <InputGroup>
            <Label htmlFor="password">New password</Label>
            <InputContainer>
              <InputIcon><Lock size={16} /></InputIcon>
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 8 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
                style={{ paddingRight: '40px' }}
              />
              <PasswordToggle
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </PasswordToggle>
            </InputContainer>
          </InputGroup>

          <InputGroup>
            <Label htmlFor="confirm">Confirm new password</Label>
            <InputContainer>
              <InputIcon><Lock size={16} /></InputIcon>
              <Input
                id="confirm"
                name="confirm"
                type={showPassword ? 'text' : 'password'}
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                maxLength={128}
                autoComplete="new-password"
              />
            </InputContainer>
          </InputGroup>

          <SubmitButton type="submit" disabled={submitting}>
            {submitting ? 'Updating...' : 'Update password'}
            {!submitting && <ArrowRight size={16} />}
          </SubmitButton>
        </Form>

        {error && (
          <ErrorBanner>
            <ShieldAlert size={18} />
            <span>{error}</span>
          </ErrorBanner>
        )}

        <FooterRow>
          <Link to="/login">&larr; Back to sign in</Link>
        </FooterRow>
      </Card>
    </Container>
  );
};

export default ResetPasswordPage;
